import { useTranslation } from 'react-i18next';

import { PublishButton } from '@/modules/drafts';
import { TooltipDisabledButton } from '@/shared/components';
import { usePermissions } from '@/shared/permissions';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

import { RailAction, type IRailPrimaryAction } from './DraftActionToolbar';

/**
 * Props for {@link AutomationRailActions}.
 */
export interface IAutomationRailActionsProps {
  automationId: string;
  /**
   * Which version Run starts, named in its label. `live` runs the published
   * version and drops Publish, which only a draft has.
   */
  version?: 'draft' | 'live';
  onRun: () => void;
  canRun: boolean;
  isRunning: boolean;
  /** Disables Run while a test is in flight. */
  isTesting: boolean;
  /** Replaces Run and Publish, e.g. the blueprint's Approve & build. */
  primaryAction?: IRailPrimaryAction;
  /** Icon-only, for the collapsed rail; labelled otherwise. */
  isCollapsed: boolean;
}

/**
 * The automation's actions at the top of its config rail, styled as on the
 * gate-off draft rail: labelled while the rail is open, icon-only once it
 * collapses. On a draft, Publish then Run, the primary action; on the live
 * version, only Run. Run's tooltip names the version it starts. Publish keeps
 * the publish permission and validation of the draft rail. The test ids match the draft rail's, which tours and
 * end-to-end tests anchor on.
 */
export function AutomationRailActions({
  automationId,
  version = 'draft',
  onRun,
  canRun,
  isRunning,
  isTesting,
  primaryAction,
  isCollapsed,
}: IAutomationRailActionsProps) {
  const { t: tAutomation } = useTranslation('automation');
  const { t: tDrafts } = useTranslation('drafts');
  const { hasPermission } = usePermissions();
  const organization = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspace = useUserPreferencesStore(selectCurrentWorkspaceId);
  const canPublish = hasPermission(['PublishAutomation'], {
    organization,
    workspace,
  });

  if (primaryAction) {
    return (
      <RailAction
        {...primaryAction}
        isCollapsed={isCollapsed}
        variant="default"
      />
    );
  }

  // `settleQuillBuild` polls this test id as its "the draft carries code"
  // signal, and the draft-creation tour points at the tour id.
  const runButton = (
    <RailAction
      label={tAutomation('automation.run.button')}
      tooltip={
        version === 'live'
          ? tAutomation('automation.run.live')
          : tAutomation('automation.run.draft')
      }
      icon="Play"
      isCollapsed={isCollapsed}
      variant="default"
      onClick={onRun}
      disabled={!canRun || isRunning || isTesting}
      isLoading={isRunning}
      data-testid="run-automation-button"
      data-tour-id="run-automation-button"
    />
  );

  if (version === 'live') return runButton;

  const publishLabel = tDrafts('drafts.actions.publish');

  return (
    <>
      {canPublish ? (
        <PublishButton
          automationId={automationId}
          renderTrigger={({ disabled, isLoading, onPublish }) => (
            <RailAction
              label={publishLabel}
              icon="Rocket"
              isCollapsed={isCollapsed}
              onClick={onPublish}
              disabled={disabled || isLoading}
              isLoading={isLoading}
              data-testid="publish-button"
              data-tour-id="run-page-publish-button"
            />
          )}
        />
      ) : (
        <TooltipDisabledButton
          tooltip={tAutomation('automation.header.tooltip.publishDisabled')}
          icon="Rocket"
          size={isCollapsed ? 'icon-sm' : 'sm'}
          data-testid="publish-button"
        >
          {!isCollapsed && publishLabel}
        </TooltipDisabledButton>
      )}
      {runButton}
    </>
  );
}
